import { useEffect, useState } from 'react';
import { availableSurfaces, type Surface } from '~/lib/surfaces';
import { adminApi } from '~/screens/job-apply/admin/api/adminClient';
import { agencyApi, setActiveAgencyId } from '~/screens/agency/api/agencyClient';

/**
 * Quién puede ver qué. Dos preguntas al servidor —¿tienes asiento en una
 * agencia?, ¿eres operador?— resueltas UNA vez y cacheadas en el módulo, con la
 * misma forma que `useIsOperator` ya usaba: el switcher se monta en cada shell,
 * y sin caché cambiar de superficie dispararía las dos llamadas de nuevo en
 * cada navegación.
 *
 * Las dos respuestas se piden con `allSettled`: que la consola de agencia esté
 * caída no puede esconderle al operador su propia consola, ni al revés. Un
 * rechazo significa "esta superficie no", nunca "la pantalla falló" — el
 * switcher es cromo, y un cromo que se rompe no debe tumbar la app.
 */
export interface SurfaceAccess {
  surfaces: Surface[];
  loading: boolean;
  /** Nombre de la agencia resuelta, para el rótulo del shell de agencia. */
  agencyName: string | null;
}

interface CacheEntry {
  agency: boolean;
  operator: boolean;
  agencyName: string | null;
  at: number;
}

let cache: CacheEntry | null = null;
let inflight: Promise<CacheEntry> | null = null;
const TTL_MS = 60_000;

/** Sólo para las pruebas: un módulo con caché global no se puede reusar sin esto. */
export function resetSurfaceCache(): void {
  cache = null;
  inflight = null;
}

function fresh(): boolean {
  return !!cache && Date.now() - cache.at < TTL_MS;
}

async function resolve(): Promise<CacheEntry> {
  if (fresh()) return cache as CacheEntry;
  if (inflight) return inflight;

  inflight = (async () => {
    const [agency, operator] = await Promise.allSettled([agencyApi.me(), adminApi.me()]);

    const hasAgency = agency.status === 'fulfilled';
    if (hasAgency) {
      // Fijar la agencia acá y no sólo en AgencyLayout: el switcher puede
      // llevar a /agency desde el shell del candidato, y esa primera pantalla
      // ya necesita la cabecera X-Agency-Id puesta.
      setActiveAgencyId(String(agency.value.agency._id));
    }

    const entry: CacheEntry = {
      agency: hasAgency,
      operator: operator.status === 'fulfilled' && !!operator.value?.is_operator,
      agencyName: hasAgency ? agency.value.agency.name : null,
      at: Date.now(),
    };
    cache = entry;
    inflight = null;
    return entry;
  })();

  return inflight;
}

export function useSurfaces(): SurfaceAccess {
  const [entry, setEntry] = useState<CacheEntry | null>(fresh() ? cache : null);
  const [loading, setLoading] = useState(!fresh());

  useEffect(() => {
    let cancelled = false;
    resolve()
      .then((next) => {
        if (cancelled) return;
        setEntry(next);
      })
      .catch(() => {
        // resolve() ya absorbe los fallos por superficie; llegar acá significa
        // que algo más se rompió, y la respuesta correcta sigue siendo "sólo
        // tienes tu panel" en vez de una pantalla de error sobre el nav.
        if (!cancelled) setEntry({ agency: false, operator: false, agencyName: null, at: Date.now() });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    surfaces: availableSurfaces({
      agency: entry?.agency ?? false,
      operator: entry?.operator ?? false,
    }),
    loading,
    agencyName: entry?.agencyName ?? null,
  };
}
